import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  TextField,
  Select,
  TableMetadata,
  useTable,
  Label,
  Typography,
  Checkbox,
  Button,
  Modal
} from "@king-ict/eupisi-ui/components";
import { PagedResult } from "@king-ict/eupisi-ui/models";
import { ExportTypeEnum } from "@king-ict/eupisi-ui/models/Export/ExportTypeEnum";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import {
  createInitialInstitutionIds,
  createInitialRequestDataSearch,
  getInitialTableState,
  canViewOutsideEnrollmentAreaDetails,
} from "./utils";
import {
  getRequestDataExport,
  searchRequestData,
  assignEnrollmentInstitution,
} from "../../api/Request/RequestApi";
import {
  ContentArea,
  InstitutionSelect,
  ListTable,
  SearchCard,
  ToolbarTop,
  FormCard,
} from "../../components";
import {
  useInput,
  useFilterPersistence,
  useAuth,
  useEnumCodeBooks,
  useTableStatePersistence,
} from "../../hooks";
import { Role } from "../../models";
import {
  OutsideEnrollmentAreaStatus,
  RequestData,
  RequestDataSearch,
  RequestSort,
} from "../../models/Request";
import { ClaimType } from "../../models/User";
import {
  useGetSchoolYearCurrentQuery,
  useGetSchoolYearListQuery,
} from "../../queries/codeBookQuery";
import { outsideEnrollmentAreaEditUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";
import { addPageSizeOptionsToTableProps } from "../../utils/tableUtils";

//STORAGE KEY
const STORAGE_KEY = "OutsideEnrollmentAreaSearch";

export function OutsideEnrollmentAreaList() {
  const { t } = useTranslation();
  const history = useHistory();
  const { handleChange } = useInput();
  const auth = useAuth();
  const { hasRoleAccess } = auth;

  const {
    requestTypeCodeBook,
    requestStatusCodeBook,
    outsideEnrollmentAreaStatusCodeBook,
  } = useEnumCodeBooks();
  const { data: schoolYear } = useGetSchoolYearCurrentQuery();
  const [selectAll, setSelectAll] = useState(false);
  const [isSuggestedEntryDecisionConfimModalVisible, setIsSuggestedEntryDecisionConfimModalVisible] = useState(false);
  const [isSuggestedEntryDecisionSuccessModalVisible, setIsSuggestedEntryDecisionSuccessModalVisible] = useState(false);

  const INITIAL_TABLE_STATE = useMemo(
    () => getInitialTableState(STORAGE_KEY),
    []
  );

  const INITIAL_INSTITUTION_IDS = useMemo(
    () => createInitialInstitutionIds(auth.currentRole),
    [auth.currentRole]
  );

  const INITIAL_REQUEST_DATA_SEARCH = useMemo(
    () => ({
      ...createInitialRequestDataSearch({
        schoolYearId: schoolYear?.id,
        initialTableState: INITIAL_TABLE_STATE,
        defaultInstitutionIds: INITIAL_INSTITUTION_IDS,
      }),
      onlyOutsideEnrollmentArea: true,
    }),
    [schoolYear?.id, INITIAL_TABLE_STATE, INITIAL_INSTITUTION_IDS]
  );

  //TABLE DATA
  const defaultTableData = useMemo(
    () => TableUtils.tableDefaultData<RequestData>(),
    []
  );
  const [tableData, setTableData] =
    useState<PagedResult<RequestData>>(defaultTableData);
  //SET SEARCH FILTERS
  const {
    filterState: requestDataSearch,
    setFilterState: setRequestDataSearch,
    saveFilters,
    clearFilters,
    filterChanged,
    setClearFilter,
  } = useFilterPersistence<RequestDataSearch>({
    storageKey: STORAGE_KEY,
    initialState: INITIAL_REQUEST_DATA_SEARCH,
  });

  //TABLE METADATA
  const tableMetadata: TableMetadata<RequestData> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "select",
        header: "",
        accessor: (item: RequestData) => {
          if (hasRoleAccess({ whitelist: [Role.CAS, Role.Helpdesk, Role.CountyAdministrator, Role.CountyLeader] })) {
            return (
              <div>
                <Checkbox
                  label={""}
                  name={nameof<typeof item>("id")}
                  value={item.id}
                  checked={item.isSelected}
                  onChange={handleRequestDataCheckedChange}
                  disabled={![OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted, OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected].includes(item.outsideEnrollmentAreaStatus!)}
                />
              </div>
            );
          }
        },
      },
      {
        columnKey: "code",
        header: t("common.schoolYearModel.code"),
        accessor: (item: RequestData) => {
          return <div className={"text-nowrap text-right"}>{item.code}</div>;
        },
        sortBy: RequestSort.Code,
      },
      {
        columnKey: "firstName",
        header:
          t("common.schoolCandidateModel.lastName") +
          "/" +
          t("common.schoolCandidateModel.firstName"),
        accessor: (item: RequestData) => {
          return (
            <div>
              <div className={"text-nowrap"}>
                {item.lastName},&nbsp;{item.firstName}
              </div>
              <div className={"text-nowrap"}>
                {t("common.schoolCandidateModel.oib")}:&nbsp;{item.childOib}
              </div>
            </div>
          );
        },
        sortBy: RequestSort.LastName,
      },
      {
        columnKey: "dateOfBirth",
        header: t("common.schoolCandidateModel.dateOfBirth"),
        accessor: (item: RequestData) => {
          return (
            <div className={"text-nowrap text-right"}>
              {item.dateOfBirth ? hrDate(new Date(item.dateOfBirth)) : ""}
            </div>
          );
        },
        sortBy: RequestSort.Birthday,
      },
      {
        columnKey: "permanentAddress",
        header:
          t("common.schoolCandidateModel.permanentAddress") +
          "/" +
          t("common.schoolCandidateModel.temporaryAddress"),
        accessor: (item: RequestData) => {
          return (
            <>
              <div className={"text-nowrap"}>
                <b>
                  {item.permanentAddress?.street}&nbsp;
                  {item.permanentAddress?.houseNumber},&nbsp;
                  {item.permanentAddress?.town}{" "}
                </b>
              </div>
              {item.temporaryAddress && (
                <div className={"text-nowrap"}>
                  {item.temporaryAddress.street}&nbsp;
                  {item.temporaryAddress.houseNumber},&nbsp;
                  {item.temporaryAddress.town}{" "}
                </div>
              )}
            </>
          );
        },
        sortBy: RequestSort.Address,
      },
      {
        columnKey: "type",
        header: t("common.requestModel.requestType"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getRequestTypeColor(item.requestType)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestTypeCodeBook.find((v) => v.id === item.requestType)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Type,
      },
      {
        columnKey: "status",
        header: t("common.requestModel.requestStatus"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getRequestStatusColor(item.status)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {requestStatusCodeBook.find((v) => v.id === item.status)?.name}
            </Label>
          );
        },
        sortBy: RequestSort.Status,
      },
      {
        columnKey: "institutionName",
        header:
          t("common.requestModel.institutionName") +
          "/" +
          t("common.requestModel.outsideEnrollmentAreaInstitutionName"),
        accessor: (item: RequestData) => {
          return (
            <>
              <div className={"text-nowrap"}>{item.institutionName}</div>
              <Typography
                className={"text-nowrap"}
                color="primary"
                component="div"
                variant="base"
              >
                {item.outsideEnrollmentAreaInstitutionName}
              </Typography>
            </>
          );
        },
      },
      {
        columnKey: "enrollmentInstitutionName",
        header: t("common.requestModel.enrollmentInstitution"),
        accessor: (item: RequestData) => {
          return (
            <div className={"text-nowrap"}>
              {item.enrollmentInstitutionName}
            </div>
          );
        },
      },
      {
        columnKey: "outsideEnrollmentAreaStatus",
        header: t("common.requestModel.outsideEnrollmentAreaStatus"),
        accessor: (item: RequestData) => {
          return (
            <Label
              color={FormatUtils.getOutsideEnrollmentAreaStatusColor(
                item.outsideEnrollmentAreaStatus
              )}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {
                outsideEnrollmentAreaStatusCodeBook.find(
                  (v) => v.id === item.outsideEnrollmentAreaStatus
                )?.name
              }
            </Label>
          );
        },
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: RequestData) => {
          if (!canViewOutsideEnrollmentAreaDetails(item, auth, schoolYear?.id))
            return;

          return (
            <div className={"text-right"}>
              <ArrowButton
                disabled={false}
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id, outsideEnrollmentAreaEditUrl)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<RequestDataSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });

  const { data: schoolYears } = useGetSchoolYearListQuery();

  const { handleDetailsClicked } = useTableStatePersistence({
    storageKey: STORAGE_KEY,
    requestDataSearch,
    tableState,
  });

  useEffect(() => {
    if (!requestDataSearch.schoolYearId && schoolYear && schoolYear.id) {
      setRequestDataSearch({
        ...requestDataSearch,
        schoolYearId: schoolYear.id,
      });
    }
  }, [schoolYear, requestDataSearch.schoolYearId]);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<RequestDataSearch>(
        requestDataSearch,
        tableState
      );
      const itemList = await searchRequestData(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleInstitutionChange = (id: number) => {
    const newSearch: RequestDataSearch = {
      ...requestDataSearch,
      institutionIds: !isNaN(id) ? [id] : [],
    };
    setRequestDataSearch(newSearch);
  };
  
  const handleSearchClicked = () => {
    doSearch();
  };
  const handleClearSearchClicked = () => {
    const newRequestDataSearch: RequestDataSearch = {
      schoolYearId: schoolYear?.id,
      institutionIds: INITIAL_INSTITUTION_IDS,
      onlyRequests: false,
      onlyWithAddReasonDelayPreviousYear: false,
      onlyNotSchoolCandidate: false,
      onlyOutsideEnrollmentArea: true,
      onlyLanguageCourseRequired: false,
      onlyMinistryLanguageCourseRequired: false,
      onlyEnrollmentApproved: false,
      onlyEnrolled: false,
      onlyOutsideEnrollmentAreaOnlyEditable: false,
      outsideEnrollmentAreaStatus:
        INITIAL_TABLE_STATE.outsideEnrollmentAreaStatus,
    };

    setClearFilter(true);
    setRequestDataSearch(newRequestDataSearch);
    clearFilters();
    doSearch();
    setSelectAll(false);
  };
  const handleDownloadClicked = async (exportType: ExportTypeEnum) => {
    await getRequestDataExport(requestDataSearch, exportType);
  };
  const doSearch = () => {
    setFilters({ ...requestDataSearch });
  };

  const handleRequestDataCheckedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const requestDataId = Number(e.target.value);
    const isChecked = e.target.checked;

    const updatedResults = tableData.results.map((val) => {
      if (val.id === requestDataId) {
        return { ...val, isSelected: isChecked };
      }
      return val;
    });

    setTableData((prevState) => ({
      ...prevState,
      results: updatedResults
    }));
  };

  const handleSelectAllCheckedChange = (e: any) => {
    const isChecked = !selectAll;
    setSelectAll(isChecked);

    const isEligibleStatus = (status: OutsideEnrollmentAreaStatus) => {
      return status === OutsideEnrollmentAreaStatus.SuggestedEnrollmentAccepted ||
        status === OutsideEnrollmentAreaStatus.SuggestedEnrollmentRejected;
    };

    const hasEligibleResults = tableData?.results?.some((x) => isEligibleStatus(x.outsideEnrollmentAreaStatus!));

    if (!hasEligibleResults) {
      return;
    }

    const updatedResults = tableData.results.map((val) => {
      const isEligible = isEligibleStatus(val.outsideEnrollmentAreaStatus!);

      return isEligible ? { ...val, isSelected: isChecked } : val;
    });

    setTableData((prevState) => ({
      ...prevState,
      results: updatedResults
    }));
  };

  const openSuggestedEntryDecisionConfimModal = () => {
    if (tableData?.results?.filter((x) => x.isSelected).length > 0) {
      setIsSuggestedEntryDecisionConfimModalVisible(true);
    }
    return;
  };

  const closeSuggestedEntryDecisionConfimModal = () => {
    setIsSuggestedEntryDecisionConfimModalVisible(false);
  };

  const handleConfirmSuggestedEntryDecisionConfirmModal = async () => {
    setIsSuggestedEntryDecisionConfimModalVisible(false);
    const selectedRequestIds: number[] = tableData.results.reduce<number[]>((acc, x) => (x.isSelected ? [...acc, x.id] : acc), []);

    await assignEnrollmentInstitution(selectedRequestIds);
    setIsSuggestedEntryDecisionSuccessModalVisible(true);
    doSearch();
    setSelectAll(false);
  };

  return (
    <ContentArea>
      <ToolbarTop
        pageTitle={t("common.requestModel.pageName")}
        showAdd={"hide"}
      />
      <SearchCard
        onClickSearch={handleSearchClicked}
        onClickClearSearch={handleClearSearchClicked}
        isFilterChanged={filterChanged}
        cardTitle={t("common.requestModel.searchCardName")}
        hideDivider={true}
      >
        <Row>
          <Col md={12}>
            <Select
              label={t("common.schoolCandidateModel.schoolYearId")}
              name={nameof<typeof requestDataSearch>("schoolYearId")}
              value={requestDataSearch.schoolYearId}
              required={true}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(schoolYears)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={8}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof requestDataSearch>("institutionIds")}
              value={
                requestDataSearch.institutionIds &&
                  requestDataSearch.institutionIds.length > 0
                  ? requestDataSearch.institutionIds[0]
                  : undefined
              }
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              excludeHandicapped
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
              showUser
              shouldCheckParentCounty
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolYearModel.code")}
              name={nameof<typeof requestDataSearch>("code")}
              value={requestDataSearch.code}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.lastName")}
              name={nameof<typeof requestDataSearch>("lastName")}
              value={requestDataSearch.lastName}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
          <Col md={4}>
            <TextField
              label={t("common.schoolCandidateModel.firstName")}
              name={nameof<typeof requestDataSearch>("firstName")}
              value={requestDataSearch.firstName}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.oib")}
              name={nameof<typeof requestDataSearch>("oib")}
              value={requestDataSearch.oib}
              onChange={handleChange(setRequestDataSearch)}
            />
          </Col>
        </Row>
        <Row>
          <Col md={3}>
            <Select
              label={t("common.requestModel.requestType")}
              name={nameof<typeof requestDataSearch>("requestType")}
              value={requestDataSearch.requestType}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(requestTypeCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
          <Col md={3}>
            <Select
              label={t("common.requestModel.requestStatus")}
              name={nameof<typeof requestDataSearch>("requestStatus")}
              value={requestDataSearch.requestStatus}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(requestStatusCodeBook)}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
          <Col md={5}>
            <Select
              label={t("common.requestModel.outsideEnrollmentAreaStatus")}
              name={nameof<typeof requestDataSearch>(
                "outsideEnrollmentAreaStatus"
              )}
              value={requestDataSearch.outsideEnrollmentAreaStatus}
              onChange={handleChange(setRequestDataSearch)}
              selectData={codeBookToSelectData(
                outsideEnrollmentAreaStatusCodeBook
              )}
              emptyOption={{ text: t("common.all"), value: undefined }}
            />
          </Col>
        </Row>
        {[Role.InstitutionAdministrator, Role.InstitutionPrincipal].includes(
          auth?.currentRole?.role ?? 0
        ) && (
            <Row>
              <Col md={4}>
                <Checkbox
                  label={t("common.requestModel.onlyEditable")}
                  name={nameof<typeof requestDataSearch>(
                    "onlyOutsideEnrollmentAreaOnlyEditable"
                  )}
                  checked={
                    requestDataSearch.onlyOutsideEnrollmentAreaOnlyEditable
                  }
                  onChange={handleChange(setRequestDataSearch)}
                />
              </Col>
            </Row>
          )}
      </SearchCard>
      {hasRoleAccess({ whitelist: [Role.CAS, Role.Helpdesk, Role.CountyAdministrator, Role.CountyLeader] }) &&
        <FormCard>
          <Row className="m-1 p-2 pt-4 bg-light border">
            <Col md={2} className="pt-1">
              <Checkbox
                label={t("common.selectAll")}
                name={"selectAll"}
                checked={selectAll}
                onChange={handleSelectAllCheckedChange}
                disabled={false}
              />
            </Col>
            <Col md={10}>
              <Button
                onClick={openSuggestedEntryDecisionConfimModal}
                size="sm"
                disabled={tableData?.results?.filter((x) => x.isSelected).length === 0}
              >
                {t("common.requestModel.suggestedEntryDecision")}
              </Button>
            </Col>
          </Row>
        </FormCard>
      }
      <ListTable
        tableProps={addPageSizeOptionsToTableProps(
          tableProps,
          [10, 25, 50, 100, 500]
        )}
        onClickDownload={(exportType: ExportTypeEnum) =>
          handleDownloadClicked(exportType)
        }
        showDownload
      />
      <Modal
        type="confirm"
        open={isSuggestedEntryDecisionConfimModalVisible}
        confirmText={t("common.requestModel.suggestedEntryDecisionConfirm")}
        cancelText={t("common.requestModel.suggestedEntryDecisionQuit")}
        onConfirm={handleConfirmSuggestedEntryDecisionConfirmModal}
        onClose={closeSuggestedEntryDecisionConfimModal}
      >
        <div>
          <Typography variant="largeBold" component="h1">
            {t("common.requestModel.suggestedEntryDecision")}
          </Typography>
          <Typography variant="medium">
            {t("common.requestModel.suggestedEntryDecisionConfirmModal")}
          </Typography>
        </div>
      </Modal>
      <Modal
        type="message"
        open={isSuggestedEntryDecisionSuccessModalVisible}
        confirmText={t("common.close")}
        onClose={() => setIsSuggestedEntryDecisionSuccessModalVisible(false)}
        onConfirm={() => setIsSuggestedEntryDecisionSuccessModalVisible(false)}
      >
        <Typography variant="medium">
          {t("common.requestModel.suggestedEntryDecisionSuccessModal")}
        </Typography>
      </Modal>
    </ContentArea>
  );
}
